/**
 * ImpersonationBanner - Shows a persistent banner when admin is impersonating a user
 */
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Eye, X, Shield, AlertTriangle } from 'lucide-react';

export const ImpersonationBanner = () => {
    const { isImpersonating, impersonationInfo, endImpersonation, isReadOnly } = useAuth();

    if (!isImpersonating || !impersonationInfo) {
        return null;
    }

    const { targetUser, originalAdminName, readOnly } = impersonationInfo;

    return (
        <div 
            className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-4 shadow-premium-md z-[100]"
            data-testid="impersonation-banner"
        >
            <div className="flex items-center gap-3">
                <Eye className="h-5 w-5" />
                <span className="font-medium">
                    Viewing as: <strong>{targetUser?.name}</strong>
                    {targetUser?.organization_name && (
                        <span className="text-amber-100"> @ {targetUser.organization_name}</span>
                    )}
                </span>
                {readOnly && (
                    <span className="flex items-center gap-1 bg-amber-600 px-2 py-0.5 rounded text-xs font-medium">
                        <Shield className="h-3 w-3" />
                        Read-only
                    </span>
                )}
                <span className="text-amber-200 text-sm">
                    (Role: {targetUser?.role})
                </span>
            </div>
            
            <div className="flex items-center gap-3">
                {readOnly && (
                    <span className="text-xs text-amber-200 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Destructive actions disabled
                    </span>
                )}
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={endImpersonation}
                    className="bg-white text-amber-600 hover:bg-amber-50 font-medium"
                    data-testid="end-impersonation-btn"
                >
                    <X className="h-4 w-4 mr-1" />
                    Exit to Admin ({originalAdminName})
                </Button>
            </div>
        </div>
    );
};

export default ImpersonationBanner;
